import identity from 'lodash/identity'
import mapValues from 'lodash/mapValues'
import pickBy from 'lodash/pickBy'
import { useEffect, useMemo, useState } from 'react'
import { ExtractRouteParams } from 'react-router'
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom'

import { config } from '@/config'

import type { routes, standaloneRoutes } from '../../app/router/routes'
import { getRoutes } from '../../app/router/routes'
import { isNativePlatform } from '../native/app'
import useNavigationHistory from './useNavigationHistory'

function pathToRegex(path: string) {
  // :param を一時的にプレースホルダーに変換
  const paramReplaced = path.replace(/:([^/]+)/g, '___PARAM___')
  // 特殊文字をエスケープ（/ のエスケープは不要なので除外）
  const escaped = paramReplaced.replace(/([.+*?=^!:${}()[\]|\\])/g, '\\$1')
  // プレースホルダーを正規表現に置き換える
  const finalPattern = escaped.replace(/___PARAM___/g, '[^/]+')
  return new RegExp(`^${finalPattern}$`)
}

/**
 * Routing用hook
 *
 */
export function useRouter<Params = {}, State = {}>() {
  const params = useParams<{ artistId?: string; itemId?: string }>()
  const location = useLocation<State>()
  const history = useHistory()
  const navigationHistory = useNavigationHistory()

  const routerPages = mapValues(
    getRoutes(config.applicationType),
    'path'
  ) as RouterPaths

  return {
    routerPages,

    routerNames: mapValues(
      getRoutes(config.applicationType),
      'name'
    ) as RouterPaths,

    router: useMemo(() => {
      return {
        push: history.push,
        replace: history.replace,
        pathname: location.pathname,
        /** ブラウザバックを実行する。ブラウザバックできない場合はデフォルトのページに遷移する */
        goBack: (defaultBackPage?: string) => {
          if (!document) return

          const artistId =
            (config?.applicationType === 'standalone'
              ? config.artistId
              : params?.artistId || config.artistId) || '#'

          if (isNativePlatform()) {
            const isFromDeeplink =
              (location.state as { fromDeeplink?: boolean })?.fromDeeplink ===
              true
            if (!location.key || history.length <= 1 || isFromDeeplink) {
              // replace current router to home
              history.replace(
                config?.applicationType === 'standalone'
                  ? generatePath(routerPages.ArtistTop, { artistId })
                  : generatePath(routerPages.GlobalHome)
              )
              if (defaultBackPage) {
                history.push(defaultBackPage)
              }
              return
            }
            history.goBack()
            return
          }

          // デフォルトページに遷移する処理
          const replaceToDefaultPage = () =>
            history.replace(
              defaultBackPage ??
                (location?.pathname?.includes('console')
                  ? routerPages.ConsoleDashboard
                  : config?.applicationType === 'standalone' ||
                    location?.pathname?.includes('artists')
                  ? generatePath(routerPages.ArtistTop, { artistId })
                  : generatePath(routerPages.GlobalHome))
            )

          // ストアアイテム詳細ページ
          const isStoreItemPage =
            params?.itemId &&
            generatePath(routerPages.ArtistStoreItemDetail, {
              artistId,
              itemId: params.itemId,
            }).includes(location.pathname)
          if (isStoreItemPage) {
            // 直近のページがアーティストトップの場合、アーティストトップに戻る
            const artistTopPath = generatePath(routerPages.ArtistTop, {
              artistId,
            })
            if (
              navigationHistory.length > 1 &&
              navigationHistory[1].includes(artistTopPath)
            ) {
              return history.push(artistTopPath)
            } else if (
              // 直近がストアコレクション詳細の場合、そのURLに戻る
              navigationHistory.length > 1 &&
              pathToRegex(routerPages.StoreCollectionDetail).test(
                navigationHistory[1]
              )
            ) {
              return tryGoBack().catch(() =>
                history.replace(navigationHistory[1])
              )
            }
            return history.push(
              generatePath(routerPages.ArtistStore, { artistId })
            )
          }

          const referrer = document.referrer

          /**
           * 以下の条件を満たす場合はブラウザバックを実行する
           * 1. referrerが存在しないが、location.keyはある
           * 2. 同アプリドメインかつ現在のページではない値のreferrerが存在し、かつhistoryが存在する
           */
          const canGoBack =
            (!referrer && !!location.key) ||
            (!!referrer &&
              referrer !== document.location.href &&
              referrer.includes(document.location.hostname) &&
              history.length > 0)

          if (!canGoBack) return replaceToDefaultPage()
          tryGoBack().catch(() => replaceToDefaultPage())
        },

        // Merge params and parsed query string into single "query" object
        // so that they can be used interchangeably.
        // Example: /:topic?sort=popular -> { topic: "react", sort: "popular" }
        query: {
          ...Object.fromEntries(new URLSearchParams(location.search)), // Convert string to object
          ...params,
        } as Params,
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, location, history]),

    /**
     * @see https://reactrouter.com/web/api/generatePath
     */
    generatePath,
    generateQueryPath,
    location,
  }
}

type RouterPaths = Record<
  keyof typeof routes & keyof typeof standaloneRoutes,
  string
>

export function useTrackPageView(
  callback: (props: { location: ReturnType<typeof useLocation> }) => void
) {
  const location = useLocation()

  useEffect(() => {
    callback({ location })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])
}

export function usePreviousPageUrl() {
  const location = useLocation()
  const [previousPath, setPreviousPath] = useState<string | null>(null)

  useEffect(() => {
    setPreviousPath(location.pathname)
  }, [location])

  return previousPath
}

/** ブラウザバックを実行し、失敗した場合はエラーを投げる */
function tryGoBack() {
  return new Promise((resolve, reject) => {
    const initialHistoryLength = history.length

    // popstateイベントリスナーを追加
    const onPopState = () => {
      // タイムアウト処理、失敗時にはスルーされる
      clearTimeout(timeoutId)
      //成功時の処理、リスナーを削除してtrueを返す
      window.removeEventListener('popstate', onPopState)
      resolve(true)
    }

    // ブラウザバックできなかった場合の処理
    const timeoutId = setTimeout(() => {
      window.removeEventListener('popstate', onPopState)
      if (history.length === initialHistoryLength) {
        reject(new Error('ブラウザバックに失敗しました。'))
      } else {
        resolve(true)
      }
    }, 50)

    // popstateイベントリスナーを追加
    window.addEventListener('popstate', onPopState)
    // ブラウザバックを試みる
    history.back()
  })
}

/**
 * redirectUri用に現在のロケーションパス（クエリパラメータとハッシュを含む）を構築
 * @returns pathname、search、hashを含む完全なパス
 * @example
 * buildRedirectUri() -> /userProfile/123/ownedItems/456?skuId=789#section
 */
export function buildRedirectUri(): string {
  if (typeof window !== 'undefined') {
    const { pathname, search, hash } = window.location
    // window.location.search/hashは存在しない場合は空文字を返すが、念のため ?? '' で防御
    return `${pathname ?? ''}${search ?? ''}${hash ?? ''}`
  }
  return '/'
}

/**
 * generate path with query string
 * @param path
 * @param params
 * @param query
 * @example
 * generateQueryPath('/test', {}, {skuId: 'xxxx'}) -> /test?skuId=xxxx
 * generateQueryPath('/test', {}, {skuId: ''}) -> /test
 */
function generateQueryPath<S extends string>(
  path: S,
  params: ExtractRouteParams<S> | undefined,
  query: Record<string, string>
): string {
  const queryUrl = new URLSearchParams(pickBy(query, identity))
  if (queryUrl.toString()) {
    return `${generatePath(path, params)}?${queryUrl.toString()}`
  }
  return generatePath(path, params)
}
